import React from 'react';
import { Button } from '../../ui/button';
import {
  Sparkles, Globe, FileCode, Database, Zap, Layers, Code, Copy, Check
} from 'lucide-react';
import { CollapsibleSection } from './CollapsibleSection';

// Result Message Component
export const ResultMessage = ({ message, expandedSections, toggleSection, copiedItems, copyToClipboard, onFollowUp, isProcessing }) => {
  const { idea, scaffold, code, id } = message;
  const suggestions = idea?.ai_suggestions;

  return (
    <div className="w-full max-w-[95%] space-y-3" data-testid={`result-message-${id}`}>
      <div className="bg-[#302f2c] border border-[#1e3a5f]/50 rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#7dd3fc]/10 to-transparent p-4 border-b border-[#1e3a5f]/50">
          <h3 className="text-[#7dd3fc] font-bold text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            {idea?.name || 'Your App'}
          </h3>
          <p className="text-[#888680] text-xs mt-1">{idea?.description}</p>
        </div>

        {/* Tech Stack */}
        {suggestions?.tech_stack && (
          <div className="p-4 border-b border-[#1e3a5f]/30">
            <h4 className="text-[#888680] text-xs font-semibold uppercase tracking-wider mb-2">Tech Stack</h4>
            <div className="grid grid-cols-3 gap-2">
              {[
                { icon: Globe, label: 'Frontend', value: suggestions.tech_stack.frontend },
                { icon: FileCode, label: 'Backend', value: suggestions.tech_stack.backend },
                { icon: Database, label: 'Database', value: suggestions.tech_stack.database }
              ].map((item) => (
                <div key={item.label} className="bg-[#1a1c1b] rounded-lg p-2 text-center">
                  <item.icon className="w-4 h-4 text-[#7dd3fc] mx-auto mb-1" />
                  <p className="text-[#7dd3fc] text-xs font-medium">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MVP Features */}
        {suggestions?.mvp_scope && (
          <div className="p-4 border-b border-[#1e3a5f]/30">
            <h4 className="text-[#888680] text-xs font-semibold uppercase tracking-wider mb-2">MVP Features</h4>
            <div className="flex flex-wrap gap-1.5">
              {suggestions.mvp_scope.map((item, idx) => (
                <span key={`mvp-${idx}-${item}`} className="bg-[#7dd3fc]/20 text-[#7dd3fc] px-2 py-0.5 rounded-full text-xs">
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Collapsible Sections */}
        {suggestions?.suggested_features && (
          <CollapsibleSection
            id={`${id}-features`}
            title="Suggested Features"
            icon={Zap}
            expanded={expandedSections[`${id}-features`]}
            toggle={() => toggleSection(`${id}-features`)}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {suggestions.suggested_features.slice(0, 4).map((feature, idx) => (
                <div key={`feat-${idx}-${feature.title}`} className="bg-[#1a1c1b] rounded-lg p-2">
                  <p className="text-[#7dd3fc] text-xs font-medium">{feature.title}</p>
                  <p className="text-[#888680] text-xs">{feature.description}</p>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {scaffold?.scaffold?.project_structure && (
          <CollapsibleSection
            id={`${id}-structure`}
            title="Project Structure"
            icon={Layers}
            expanded={expandedSections[`${id}-structure`]}
            toggle={() => toggleSection(`${id}-structure`)}
          >
            <div className="bg-[#1a1c1b] rounded-lg p-3 font-mono text-xs max-h-40 overflow-y-auto">
              {scaffold.scaffold.project_structure.slice(0, 15).map((item, idx) => (
                <div key={`struct-${idx}-${item.path}`} className="text-[#888680] py-0.5">
                  {item.type === 'folder' ? '📁' : '📄'} {item.path}
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {code && (
          <CollapsibleSection
            id={`${id}-code`}
            title={`Code: ${code.filename}`}
            icon={Code}
            expanded={expandedSections[`${id}-code`] !== false}
            toggle={() => toggleSection(`${id}-code`)}
            defaultExpanded
          >
            <div className="relative">
              <Button
                onClick={() => copyToClipboard(code.code, `${id}-code`)}
                variant="ghost"
                data-testid={`copy-result-code-${id}`}
                className="absolute top-1 right-1 text-[#888680] hover:text-[#7dd3fc] p-1.5 h-auto"
              >
                {copiedItems[`${id}-code`] ? <Check size={12} /> : <Copy size={12} />}
              </Button>
              <div className="bg-[#1a1c1b] rounded-lg p-3 overflow-x-auto max-h-48">
                <pre className="text-[#888680] text-xs font-mono whitespace-pre">{code.code}</pre>
              </div>
            </div>
          </CollapsibleSection>
        )}
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-1.5">
        {[
          { label: 'API Endpoints', type: 'api_endpoint', lang: 'python', fw: 'fastapi', icon: FileCode },
          { label: 'DB Schema', type: 'db_schema', lang: 'python', fw: 'mongodb', icon: Database },
          { label: 'Dashboard', type: 'react_component', lang: 'javascript', fw: 'react', icon: Layers }
        ].map((action) => (
          <Button
            key={action.type}
            onClick={() => onFollowUp('generate-more-code', {
              type: action.type,
              prompt: `${action.label} for ${idea?.name}`,
              language: action.lang,
              framework: action.fw
            })}
            disabled={isProcessing}
            variant="outline"
            data-testid={`quick-action-${action.type}`}
            className="border-[#1e3a5f] text-[#888680] hover:text-[#7dd3fc] hover:border-[#7dd3fc]/50 rounded-full text-xs px-2 py-1 h-auto"
          >
            <action.icon size={12} className="mr-1" />
            {action.label}
          </Button>
        ))}
      </div>
    </div>
  );
};
